export const ADMIN_DEBUG_FLAGS = [
  {
    key: "guideTranslateAllLanguages",
    label: "Guide translate buttons",
    description: "Show the admin-only \"Translate ... in all languages\" buttons on public guide pages.",
    defaultEnabled: true,
  },
  {
    key: "tripTiming",
    label: "Trip page timing",
    description: "Show the admin-only Timing button and timing modal on public trip pages.",
    defaultEnabled: true,
  },
  {
    key: "overflowInspector",
    label: "Overflow inspector",
    description: "Show the admin-only overflow inspector button in the top navigation bar.",
    defaultEnabled: true,
  },
] as const;

export type AdminDebugFlagKey = (typeof ADMIN_DEBUG_FLAGS)[number]["key"];

export const ADMIN_DEBUG_FLAGS_STORAGE_KEY = "teravia:admin-debug-flags";
export const ADMIN_DEBUG_FLAGS_EVENT = "teravia:admin-debug-flags-changed";

export type AdminDebugFlagState = Record<AdminDebugFlagKey, boolean>;

export function defaultAdminDebugFlags(): AdminDebugFlagState {
  return ADMIN_DEBUG_FLAGS.reduce((acc, flag) => {
    acc[flag.key] = flag.defaultEnabled;
    return acc;
  }, {} as AdminDebugFlagState);
}

function normalizeAdminDebugFlags(raw: unknown): AdminDebugFlagState {
  const defaults = defaultAdminDebugFlags();
  if (!raw || typeof raw !== "object") return defaults;
  const obj = raw as Record<string, unknown>;
  for (const flag of ADMIN_DEBUG_FLAGS) {
    if (typeof obj[flag.key] === "boolean") defaults[flag.key] = obj[flag.key] as boolean;
  }
  return defaults;
}

export function readAdminDebugFlags(): AdminDebugFlagState {
  if (typeof window === "undefined") return defaultAdminDebugFlags();
  try {
    const raw = window.localStorage.getItem(ADMIN_DEBUG_FLAGS_STORAGE_KEY);
    if (!raw) return defaultAdminDebugFlags();
    return normalizeAdminDebugFlags(JSON.parse(raw));
  } catch {
    return defaultAdminDebugFlags();
  }
}

export function readAdminDebugFlag(key: AdminDebugFlagKey): boolean {
  return readAdminDebugFlags()[key];
}

export function writeAdminDebugFlags(flags: AdminDebugFlagState): void {
  if (typeof window === "undefined") return;
  const normalized = normalizeAdminDebugFlags(flags);
  window.localStorage.setItem(ADMIN_DEBUG_FLAGS_STORAGE_KEY, JSON.stringify(normalized));
  try {
    document.cookie = `teravia_admin_debug_flags=${encodeURIComponent(JSON.stringify(normalized))}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    // ignore cookie write failures
  }
  window.dispatchEvent(new CustomEvent(ADMIN_DEBUG_FLAGS_EVENT, { detail: normalized }));
}

export function writeAdminDebugFlag(key: AdminDebugFlagKey, enabled: boolean): void {
  const next = readAdminDebugFlags();
  next[key] = enabled;
  writeAdminDebugFlags(next);
}
